import React from 'react';
import styled from 'styled-components';

import { colors } from '../../../core';
import Label from '../Label';
import Text from '../Text';

export interface TextAreaInputProps {
  id?: string;
  disabled?: boolean;
  label?: string;
  onChange: (changeEvent: any) => void;
  rows?: number;
  width?: string;
  placeholder?: string;
  defaultValue?: string;
  onKeyPress?: (e: any) => void;
  errorMessage?: string;
}

const Container = styled.div<{ width: string }>`
  width: ${({ width }) => width};
`;

const StyledTextArea = styled.textarea<{ isError?: boolean }>`
  -webkit-appearance: none;
  border: ${({ isError, theme }) =>
    `1px solid ${isError ? theme.errorColor : theme.textAreaInputBorderColor || colors.smoke}`};
  border-radius: 3px;
  color: ${({ theme }) => theme.textAreaInputTextColor || colors.ash};
  display: block;
  font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
  font-size: ${({ theme }) => theme.fontSize || '14px'};
  font-weight: ${({ theme }) => theme.fontWeight || 600};
  padding: 8px 10px;
  resize: vertical;
  width: 100%;

  ::placeholder {
    color: ${({ theme }) => theme.textAreaInputPlaceholderColor || colors.smoke};
  }

  &:hover {
    box-shadow: 0 2px 4px 0 rgba(217, 217, 217, 0.5);
  }

  &:active,
  &:focus {
    color: ${({ theme }) => theme.textAreaInputFocusTextColor || colors.charcoal};
    border-color: ${({ theme }) => theme.textAreaInputFocusBorderColor || colors.primary};
    outline: 0;
  }

  &:disabled {
    background-color: ${colors.pearl};
    border-color: ${({ theme }) => theme.textAreaInputDisabledBorderColor || colors.cloud};
    box-shadow: none;
    color: ${({ theme }) => theme.textAreaInputDisabledTextColor || colors.smoke};
    cursor: default;
  }
`;

const StyledLabel = styled(Label)`
  ${({ theme }) =>
    theme.textInputShowAnimatedLabel &&
    `
      font-size: 12px;
      font-weight: 400;
      padding: 0 16px;
      margin: 0;
      position: absolute;
      top: -8px;
      line-height: 1.5;
    `}
`;

const ErrorMessage = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.errorColor};
  padding: ${({ theme }) => theme.inputErrorMessagePadding || '0'};
  line-height: 1.5;
`;

const TextAreaInput = ({
  label,
  onChange,
  rows,
  width,
  errorMessage,
  ...inputProps
}: TextAreaInputProps) => (
  <Container width={width || '100%'}>
    {label && <StyledLabel htmlFor={inputProps.id}>{label}</StyledLabel>}
    <StyledTextArea
      {...inputProps}
      rows={rows}
      onChange={(changeEvent) => onChange(changeEvent.target.value)}
      isError={!!errorMessage}
    />
    {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
  </Container>
);

TextAreaInput.defaultProps = {
  rows: 3,
};

export default TextAreaInput;
